import React from 'react'
import type { KambiSportName } from '@mobi/api-types/kambi/sports'
import { Icon, IconName } from '@mobi/component-library/Assets/Icon/Icon'
import { E_SOCCER_GROUP_ID } from '@mobi/sports-offering/helpers/reformatSportsName'

type SportsIconProps = {
  sport?: KambiSportName | null | undefined
  size?: React.ComponentProps<typeof Icon>['size']
  color?: React.ComponentProps<typeof Icon>['color']
  groupId?: number
}

export const SportsIcon: React.FC<SportsIconProps> = ({
  sport,
  size = '1.4rem',
  color,
  groupId,
}) => {
  const sportName = groupId === E_SOCCER_GROUP_ID ? 'ESPORTS' : sport
  const iconName = (sportName && kambiSportToIconMap[sportName]) || 'SportsDefault'
  return <Icon size={size} name={iconName} color={color} />
}

// =============
// Local Helpers
// =============

const kambiSportToIconMap: Partial<Record<KambiSportName, IconName>> = {
  AMERICAN_FOOTBALL: 'SportsAmericanFootball',
  ASIAN_GAMES: 'SportsAsianGames',
  ATHLETICS: 'SportsAthletics',
  AUSTRALIAN_RULES: 'SportsAustralianRules',
  BADMINTON: 'SportsBadminton',
  BANDY: 'SportsBandy',
  BASEBALL: 'SportsBaseball',
  BASKETBALL: 'SportsBasketball',
  BEACH_SOCCER: 'SportsBeachSoccer',
  BEACH_VOLLEYBALL: 'SportsBeachVolley',
  BOWLING: 'SportsBowling',
  BOWLS: 'SportsBowls',
  BOXING: 'SportsBoxing',
  BRANNBOLL: 'SportsBrannboll',
  BRIDGE: 'SportsBridge',
  CHESS: 'SportsChess',
  COMMONWEALTH_GAMES: 'SportsCommonwealthGames',
  CRICKET: 'SportsCricket',
  CURLING: 'SportsCurling',
  CYCLING: 'SportsCycling',
  CYCLO_CROSS: 'SportsCycloCross',
  DARTS: 'SportsDarts',
  DISC_GOLF: 'SportsDiscGolf',
  DIVING: 'SportsDiving',
  DRONE_RACING: 'SportsDroneRacing',
  EQUESTRIAN: 'SportsEquestrian',
  FIELD_HOCKEY: 'SportsFieldHockey',
  FIGURE_SKATING: 'SportsFigureSkating',
  FLOORBALL: 'SportsFloorball',
  FOOTBALL: 'SportsFootball',
  FORMULA_1: 'SportsFormula1',
  FORTUNE_TELLER: 'SportsFortuneTeller',
  FUTSAL: 'SportsFutsal',
  GAA_FOOTBALL: 'SportsGaelicSports',
  GAA_HURLING: 'SportsGaelicSports',
  GALLOPS: 'SportsGallops',
  GOLF: 'SportsGolf',
  GREYHOUNDS: 'SportsGreyhounds',
  HANDBALL: 'SportsHandball',
  HAPPY_HOUR: 'SportsHappyHour',
  HORSE_RACING: 'SportsHorseRacing',
  ICE_HOCKEY: 'SportsIceHockey',
  INDOOR_FOOTBALL: 'SportsIndoorFootball',
  INDOOR_HOCKEY: 'SportsIndoorHockey',
  INLINE_HOCKEY: 'SportsInlineHockey',
  INTERNATIONAL_RULES: 'SportsInternationalRules',
  LACROSSE: 'SportsLacrosse',
  MEDITERRANEAN_GAMES: 'SportsMediterraneanGames',
  MOTORSPORTS: 'SportsMotorsports',
  MOUNTAIN_BIKE: 'SportsMountainBike',
  NETBALL: 'SportsNetball',
  OLYMPIC_GAMES: 'SportsOlympicGames',
  ORIENTEERING: 'SportsOrienteering',
  PADEL_TENNIS: 'SportsPadel',
  PESAPALLO: 'SportsPesapallo',
  PICKLEBALL: 'SportsPickleball',
  POKER: 'SportsPoker',
  POLITICS: 'SportsPolitics',
  POOL: 'SportsPool',
  RACKETLON: 'SportsRacketlon',
  RINK_HOCKEY: 'SportsRinkHockey',
  ROLLER_HOCKEY: 'SportsRollerHockey',
  ROWING: 'SportsRowing',
  RUGBY_LEAGUE: 'SportsRugbyLeague',
  RUGBY_UNION: 'SportsRugbyUnion',
  SNOOKER: 'SportsSnooker',
  SOFTBALL: 'SportsSoftball',
  SPECIAL_BETS: 'SportsTabtouchExclusivesRwwa',
  SQUASH: 'SportsSquash',
  SUMO: 'SportsSumo',
  SURFING: 'SportsSurfing',
  SWIMMING: 'SportsSwimming',
  TABLE_TENNIS: 'SportsTableTennis',
  TABLEHOCKEY: 'SportsTablehockey',
  TABTOUCH_EXCLUSIVES_RWWA: 'SportsTabtouchExclusivesRwwa',
  TENNIS: 'SportsTennis',
  TROTTING: 'SportsTrotting',
  TV_NOVELTY: 'SportsTvNovelty',
  MARTIAL_ARTS: 'SportsUfcMma', // TODO: confirm this
  UNIVERSIADE: 'SportsUniversiade',
  VOLLEYBALL: 'SportsVolleyball',
  WATER_POLO: 'SportsWaterPolo',
  WEIGHTLIFTING: 'SportsWeightlifting',
  WINTER_OLYMPIC_GAMES: 'SportsWinterOlympicGames',
  WINTERSPORTS: 'SportsWinterSports',
  'WINTER-XGAMES': 'SportsWinterXGames',
  WRESTLING: 'SportsWrestling',
  YACHTING: 'SportsYachting',

  // ESPORTS
  AGE_OF_EMPIRES: 'SportsEsports',
  APEX_LEGENDS: 'SportsEsports',
  ARENA_OF_VALOR: 'SportsEsports',
  ARTIFACT: 'SportsEsports',
  CALL_OF_DUTY: 'SportsEsports',
  COUNTER_STRIKE: 'SportsEsports',
  DOTA: 'SportsEsports',
  DOTA_UNDERLORDS: 'SportsEsports',
  DRAGON_BALL: 'SportsEsports',
  ESPORTS: 'SportsEsports',
  E_BASKETBALL: 'SportsEsports',
  FARMING_SIMULATOR: 'SportsEsports',
  FIFA: 'SportsEsports',
  FORTNITE: 'SportsEsports',
  GRANBLUE_FANTASY: 'SportsEsports',
  HALO: 'SportsEsports',
  HEARTHSTONE: 'SportsEsports',
  HEROES_OF_NEWERTH: 'SportsEsports',
  HEROES_OF_THE_STORM: 'SportsEsports',
  KING_OF_GLORY: 'SportsEsports',
  LEAGUE_OF_LEGENDS: 'SportsEsports',
  MAGIC_THE_GATHERING: 'SportsEsports',
  MOBILE_LEGENDS: 'SportsEsports',
  NBA_2K: 'SportsEsports',
  OVERWATCH: 'SportsEsports',
  POKEMON: 'SportsEsports',
  PUBG: 'SportsEsports',
  QUAKE: 'SportsEsports',
  RAINBOW_SIX: 'SportsEsports',
  ROCKET_LEAGUE: 'SportsEsports',
  SMITE: 'SportsEsports',
  STARCRAFT: 'SportsEsports',
  STREET_FIGHTER: 'SportsEsports',
  SUPER_SMASH_BROS: 'SportsEsports',
  TEAMFIGHT_TACTICS: 'SportsEsports',
  TEKKEN: 'SportsEsports',
  VAINGLORY: 'SportsEsports',
  VALORANT: 'SportsEsports',
  WARCRAFT: 'SportsEsports',
  WORLD_OF_TANKS: 'SportsEsports',
}

// cspell:words BRANNBOLL FLOORBALL FUTSAL MOTORSPORTS PADEL PESAPALLO PICKLEBALL RACKETLON
// cspell:words TABLEHOCKEY UNIVERSIADE WINTERSPORTS XGAMES
